import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authService } from '@/services/auth';
import type { AdminAuthState } from '@/types/admin';
import { createLogger } from '@/utils/logger';
import { AdminAuthContext, type AdminAuthContextValue, type AdminAuthSnapshot } from './AdminAuthShared';

const logger = createLogger('AdminAuthProvider');

// Module-level bootstrap guards so dev React.StrictMode doesn't double-trigger init/verify.
let bootstrapPromise: Promise<void> | null = null;
let bootstrapComplete = false;
let bootstrapSnapshot: AdminAuthSnapshot | null = null;

function readSnapshot(): AdminAuthSnapshot {
  return {
    isAuthenticated: authService.isAuthenticated(),
    user: authService.getCurrentUser(),
    token: authService.getToken(),
  };
}

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AdminAuthState>(() => ({
    isAuthenticated: bootstrapSnapshot?.isAuthenticated ?? false,
    user: bootstrapSnapshot?.user ?? null,
    token: bootstrapSnapshot?.token ?? null,
    isLoading: !bootstrapComplete,
    error: null,
  }));

  const refreshTimeoutRef = useRef<number | null>(null);

  const refreshState = useCallback((opts?: { debounce?: boolean }) => {
    const debounce = opts?.debounce ?? true;

    const apply = () => {
      const snapshot = readSnapshot();
      bootstrapSnapshot = snapshot;
      setAuthState(prev => ({
        ...prev,
        ...snapshot,
        isLoading: false,
        error: null,
      }));
    };

    if (!debounce) {
      apply();
      return;
    }

    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = window.setTimeout(apply, 10);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        if (bootstrapComplete && bootstrapSnapshot) {
          // Already bootstrapped (common in dev StrictMode remount).
          if (!isMounted) return;
          setAuthState(prev => ({
            ...prev,
            ...bootstrapSnapshot!,
            isLoading: false,
            error: null,
          }));
          return;
        }

        if (!isMounted) return;
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

        if (!bootstrapPromise) {
          bootstrapPromise = (async () => {
            await authService.initialize();
            if (authService.isAuthenticated()) {
              await authService.refreshUserProfile(); // hits /admin/verify once per app load
            }
          })();
        }

        await bootstrapPromise;

        const snapshot = readSnapshot();
        bootstrapSnapshot = snapshot;
        bootstrapComplete = true;

        if (!isMounted) return;
        setAuthState(prev => ({
          ...prev,
          ...snapshot,
          isLoading: false,
          error: null,
        }));
      } catch (error) {
        logger.error('Auth initialization failed:', error);
        bootstrapComplete = true;
        bootstrapSnapshot = { isAuthenticated: false, user: null, token: null };

        if (!isMounted) return;
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Authentication initialization failed',
        });
      }
    };

    initializeAuth();

    const onAuthChange = () => refreshState({ debounce: true });
    window.addEventListener('auth-change', onAuthChange);

    return () => {
      isMounted = false;
      window.removeEventListener('auth-change', onAuthChange);
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [refreshState]);

  const signIn = useCallback(async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      const user = await authService.signIn();
      const token = authService.getToken();

      const snapshot = { isAuthenticated: true, user, token };
      bootstrapSnapshot = snapshot;

      setAuthState(prev => ({
        ...prev,
        ...snapshot,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      logger.error('Sign in failed:', error);
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Sign in failed',
      }));
      throw error;
    }
  }, []);

  const signOut = useCallback((): void => {
    authService.signOut();
    bootstrapSnapshot = { isAuthenticated: false, user: null, token: null };
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false,
      error: null,
    });
  }, []);

  const clearError = useCallback((): void => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  const refreshAuth = useCallback((): void => {
    const snapshot = readSnapshot();
    bootstrapSnapshot = snapshot;
    setAuthState(prev => ({
      ...prev,
      ...snapshot,
      error: !snapshot.isAuthenticated && prev.isAuthenticated ? 'Session expired' : prev.error,
    }));
  }, []);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    return authService.getAuthHeader();
  }, []);

  const value: AdminAuthContextValue = useMemo(
    () => ({
      isAuthenticated: authState.isAuthenticated,
      user: authState.user,
      token: authState.token,
      isLoading: authState.isLoading,
      error: authState.error,
      signIn,
      signOut,
      clearError,
      refreshAuth,
      getAuthHeaders,
    }),
    [authState, signIn, signOut, clearError, refreshAuth, getAuthHeaders],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};


